/**
 * ⚠️ TEMPORARY MOCK DATA — NOT FOR PRODUCTION ⚠️
 *
 * Rich sample data for the AlternativeTransport left/right panels so the UI can
 * be styled without a live backend.
 *
 * HOW TO REMOVE (all in one place):
 *  1. Delete this file.
 *  2. In `AlternativeTransportDetails.tsx`, remove the `USE_MOCK_DATA` block
 *     (the import + the early-return guard marked with `MOCK DATA`).
 *
 * Nothing else references this file.
 */
import {
  AlternativeTransportItineraryStop,
  AlternativeTransportLog,
  AlternativeTransportVehicle,
} from "shared/types/alternativeTransport";

const VEHICLE_ID = "11111111-2222-3333-4444-555555555555";

const at = (h: number, m: number, dayOffset = 0): Date => {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(h, m, 0, 0);
  return d;
};

const stop = (
  name: string,
  nsrCode: string,
  arr: Date,
  dep: Date,
  activity: AlternativeTransportItineraryStop["activity"] = "BOARDING_AND_DISEMBARKING",
): AlternativeTransportItineraryStop => ({
  name,
  nsrCode,
  stopReference: {
    nsrCode,
    uicCode: null,
    jbvId: null,
    hastusCode: null,
    nsrStopPlace: name,
  },
  activity,
  scheduledArrivalTime: arr,
  scheduledDepartureTime: dep,
  estimatedArrivalTime: arr,
  estimatedDepartureTime: dep,
  actualArrivalTime: null,
  actualDepartureTime: null,
  secondsTravelTimeToNextStop: 300,
  isArrivalCancelled: false,
  isDepartureCancelled: false,
});

const mockStops: AlternativeTransportItineraryStop[] = [
  stop("Skien stasjon", "NSR:StopPlace:1", at(10, 0), at(10, 3), "BOARDING"),
  stop("Porsgrunn stasjon", "NSR:StopPlace:2", at(10, 15), at(10, 18)),
  stop("Bamble rådhus", "NSR:StopPlace:5", at(10, 30), at(10, 32)),
  stop("Stathelle", "NSR:StopPlace:6", at(10, 38), at(10, 40)),
  stop("Larvik stasjon", "NSR:StopPlace:3", at(10, 45), at(10, 48)),
  stop("Sandefjord stasjon", "NSR:StopPlace:7", at(11, 2), at(11, 5)),
  stop("Stokke stasjon", "NSR:StopPlace:8", at(11, 12), at(11, 14)),
  stop(
    "Tønsberg stasjon",
    "NSR:StopPlace:4",
    at(11, 20),
    at(11, 20),
    "DISEMBARKING",
  ),
];

const mockReturnStops: AlternativeTransportItineraryStop[] = [
  stop("Tønsberg stasjon", "NSR:StopPlace:4", at(12, 0), at(12, 3), "BOARDING"),
  stop("Stokke stasjon", "NSR:StopPlace:8", at(12, 9), at(12, 11)),
  stop("Sandefjord stasjon", "NSR:StopPlace:7", at(12, 18), at(12, 21)),
  stop("Larvik stasjon", "NSR:StopPlace:3", at(12, 35), at(12, 38)),
  stop("Porsgrunn stasjon", "NSR:StopPlace:2", at(13, 5), at(13, 8)),
  stop(
    "Skien stasjon",
    "NSR:StopPlace:1",
    at(13, 20),
    at(13, 20),
    "DISEMBARKING",
  ),
];

const mockLogs: AlternativeTransportLog[] = [
  {
    id: "log-1",
    message: "Bussen er bestilt og bekreftet hos operatør.",
    tags: ["bestilling"],
    timestamp: at(9, 2).toISOString(),
    sourceSystem: { author: "Ola Nordmann", name: "DROPS" },
    vehicleId: VEHICLE_ID,
    locationName: "Skien stasjon",
  },
  {
    id: "log-2",
    message: "Sjåfør har meldt seg klar. Avgang som planlagt.",
    tags: [],
    timestamp: at(9, 30).toISOString(),
    sourceSystem: { author: "Kari Nordmann", name: "DROPS" },
    vehicleId: VEHICLE_ID,
    locationName: "Skien stasjon",
  },
  {
    id: "log-3",
    message:
      "Sjåfør bekreftet kapasitet på 50 passasjerer. Rullestolplass tilgjengelig.",
    tags: ["kapasitet", "universell utforming"],
    timestamp: at(9, 45).toISOString(),
    sourceSystem: { author: "Kari Nordmann", name: "DROPS" },
    vehicleId: VEHICLE_ID,
    locationName: "Skien stasjon",
  },
  {
    id: "log-4",
    message: "Forsinkelse på 3 minutter grunnet trafikk ved Porsgrunn.",
    tags: ["forsinkelse"],
    timestamp: at(10, 12).toISOString(),
    sourceSystem: { author: null, name: "DROPS" },
    vehicleId: VEHICLE_ID,
    locationName: "Porsgrunn stasjon",
  },
  {
    id: "log-5",
    message: "Ekstra passasjerer tatt om bord ved Stathelle.",
    tags: [],
    timestamp: at(10, 40).toISOString(),
    sourceSystem: { author: "Per Hansen", name: "DROPS" },
    vehicleId: VEHICLE_ID,
    locationName: "Stathelle",
  },
  {
    id: "log-6",
    message:
      "Melding fra sjåfør: kø på E18 ved Sandefjord, forventer 5 min ekstra forsinkelse.",
    tags: ["forsinkelse", "trafikk"],
    timestamp: at(11, 0).toISOString(),
    sourceSystem: { author: null, name: "APC" },
    vehicleId: VEHICLE_ID,
    locationName: "Sandefjord stasjon",
  },
  {
    id: "log-7",
    message: "Buss ankommet Tønsberg. Alle passasjerer av.",
    tags: ["ankomst"],
    timestamp: at(11, 22).toISOString(),
    sourceSystem: { author: "Per Hansen", name: "DROPS" },
    vehicleId: VEHICLE_ID,
    locationName: "Tønsberg stasjon",
  },
  {
    id: "log-8",
    message: "Retur mot Skien startet som planlagt.",
    tags: [],
    timestamp: at(12, 3).toISOString(),
    sourceSystem: { author: "Ola Nordmann", name: "DROPS" },
    vehicleId: VEHICLE_ID,
    locationName: "Tønsberg stasjon",
  },
];

export const MOCK_ALTERNATIVE_TRANSPORT_VEHICLE: AlternativeTransportVehicle = {
  id: VEHICLE_ID,
  type: "BUS",
  serviceType: "PLANNED",
  trainIds: ["1017", "815"],
  itinerary: {
    stops: mockStops,
    returnStops: mockReturnStops,
    firstStopName: "Skien stasjon",
  },
  originStopName: "Skien stasjon",
  firstStopName: "Skien stasjon",
  destinationStopName: "Tønsberg stasjon",
  scheduledDepartureTimeOrigin: at(10, 0),
  scheduledArrivalTimeDestination: at(11, 20),
  lineNames: ["R11"],
  logs: mockLogs,
  operatorId: "OP-BOREAL",
  operatorName: "Boreal Buss AS",
  currentStopReference: {
    nsrCode: "NSR:StopPlace:2",
    uicCode: null,
    jbvId: null,
    hastusCode: null,
    nsrStopPlace: "Porsgrunn stasjon",
  },
  driverPhoneNumber: "+47 999 88 777",
  workShiftPlan: {
    isirkId: "ISIRK-4567",
    shiftId: "067-1017-815",
    shiftDate: at(0, 0),
    workShiftTrips: [
      {
        vehicleUuid: VEHICLE_ID,
        tripPatternId: "TP-1",
        originStopName: "Skien stasjon",
        destinationStopName: "Porsgrunn stasjon",
        firstStopName: "Skien stasjon",
        scheduledDepartureTimeOrigin: at(10, 0),
        scheduledArrivalTimeDestination: at(10, 15),
        trainIds: ["1017"],
        stopReferences: null,
      },
      {
        vehicleUuid: VEHICLE_ID,
        tripPatternId: "TP-2",
        originStopName: "Porsgrunn stasjon",
        destinationStopName: "Larvik stasjon",
        firstStopName: "Porsgrunn stasjon",
        scheduledDepartureTimeOrigin: at(10, 18),
        scheduledArrivalTimeDestination: at(10, 45),
        trainIds: ["1017"],
        stopReferences: null,
      },
      {
        vehicleUuid: VEHICLE_ID,
        tripPatternId: "TP-3",
        originStopName: "Larvik stasjon",
        destinationStopName: "Tønsberg stasjon",
        firstStopName: "Larvik stasjon",
        scheduledDepartureTimeOrigin: at(10, 48),
        scheduledArrivalTimeDestination: at(11, 20),
        trainIds: ["815"],
        stopReferences: null,
      },
      {
        vehicleUuid: VEHICLE_ID,
        tripPatternId: "TP-4",
        originStopName: "Tønsberg stasjon",
        destinationStopName: "Sandefjord stasjon",
        firstStopName: "Tønsberg stasjon",
        scheduledDepartureTimeOrigin: at(12, 0),
        scheduledArrivalTimeDestination: at(12, 18),
        trainIds: ["815"],
        stopReferences: null,
      },
      {
        vehicleUuid: VEHICLE_ID,
        tripPatternId: "TP-5",
        originStopName: "Sandefjord stasjon",
        destinationStopName: "Larvik stasjon",
        firstStopName: "Sandefjord stasjon",
        scheduledDepartureTimeOrigin: at(12, 21),
        scheduledArrivalTimeDestination: at(12, 35),
        trainIds: ["1017"],
        stopReferences: null,
      },
      {
        vehicleUuid: VEHICLE_ID,
        tripPatternId: "TP-6",
        originStopName: "Larvik stasjon",
        destinationStopName: "Skien stasjon",
        firstStopName: "Larvik stasjon",
        scheduledDepartureTimeOrigin: at(12, 38),
        scheduledArrivalTimeDestination: at(13, 20),
        trainIds: ["1017"],
        stopReferences: null,
      },
    ],
  },
  tripPatternId: "TP-1",
  isPartOfShuttlePlan: true,
  hastusShuttlePlanId: "SHUTTLE-2026-042",
  vehicleStatus: "CONFIRMED",
  notes: [
    "Innstilt tog R11 grunnet signalfeil. Buss for tog er satt opp på hele strekningen.",
    "Ekstra kapasitet bestilt for rushtrafikk mellom Skien og Porsgrunn.",
    "Merk: Bamble rådhus og Stathelle betjenes kun på utreise, ikke på retur.",
    "Sjåfør har direktenummer ved spørsmål om ombordstigning for rullestolbrukere.",
  ],
};
